import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, CheckCircle, GraduationCap, Globe, MapPin, CloudRain, Leaf, Users, UtensilsCrossed, Clock, Video } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import claraPortrait from "@/assets/clara-portrait-final.png";
import heroBackground from "@/assets/hero-background.jpg";

export const faqItems = [
  {
    question: "Você atende presencialmente em Melbourne?",
    answer: "Meu atendimento presencial é em Brisbane, no consultório em Hamilton. Para quem mora em Melbourne, o atendimento é online, por videochamada. A qualidade do acompanhamento é a mesma, com a praticidade de não precisar se deslocar."
  },
  {
    question: "O plano alimentar considera o clima de Melbourne?",
    answer: "Sim. Melbourne tem um clima bem diferente do resto da Austrália, com invernos frios. Eu ajusto o plano para incluir refeições mais quentes e reconfortantes no inverno e opções mais leves no verão."
  },
  {
    question: "O plano inclui produtos de mercados de Melbourne?",
    answer: "Sim. Além do Coles e do Woolworths, Melbourne tem o Queen Victoria Market, o South Melbourne Market e muitos mercados de bairro. O plano é montado com produtos que você encontra nesses mercados."
  },
  {
    question: "Como funciona a primeira consulta?",
    answer: "É uma conversa de cerca de 30 minutos, sem custo, por videochamada. Eu entendo seus objetivos e sua rotina em Melbourne e explico como funciona o acompanhamento. Depois você decide, com calma, se quer seguir."
  },
  {
    question: "Como funciona o fuso horário?",
    answer: "Melbourne e Brisbane estão no mesmo fuso (AEST), exceto de outubro a abril, no horário de verão de Victoria, quando Melbourne fica 1 hora à frente. Nesse período meus horários em Melbourne são: terça e sexta das 10h às 19h, quarta e quinta das 10h às 18h e sábado das 11h às 15h. A agenda online já mostra tudo no seu fuso."
  },
  {
    question: "Você já atende pacientes em Melbourne?",
    answer: "Sim. Parte dos meus pacientes mora em Melbourne e em outras cidades de Victoria e é atendida só online. O acompanhamento foi desenhado para isso: videochamada, plano alimentar na plataforma e WhatsApp entre as consultas, nos horários que cabem na rotina de quem está aí."
  },
  {
    question: "Tem reembolso do Medicare ou do plano de saúde?",
    answer: "Medicare não: o reembolso do Medicare é exclusivo de dietitians credenciadas (APD), e eu sou nutricionista. Plano de saúde privado depende do seu fundo e do pacote de extras; eu forneço o recibo e você confere a cobertura com o plano. Meus valores já são pensados para funcionar sem reembolso."
  },
  {
    question: "O plano considera a rotina dos brasileiros em Melbourne?",
    answer: "Sim. Muitos brasileiros em Melbourne trabalham em hospitality, delivery ou construção civil. Outros estudam e trabalham ao mesmo tempo. O plano é montado de forma prática para se encaixar nessa realidade."
  },
  {
    question: "Posso substituir ingredientes por opções veganas ou vegetarianas?",
    answer: "Sim. Melbourne tem muitas opções plant-based. Se você segue uma alimentação vegetariana ou vegana, eu adapto o plano para incluir as opções que você encontra na cidade."
  },
  {
    question: "Quanto tempo leva para ver resultados?",
    answer: "Os acompanhamentos normalmente têm um período mínimo de 3 meses, mas o tempo para ver resultados depende de cada pessoa e de cada plano. Muitos pacientes relatam, já nas primeiras semanas, mais disposição e uma relação mais tranquila com a comida."
  },
];

const desafiosMelbourne = [
  {
    icon: CloudRain,
    title: "Clima imprevisível e comfort food",
    description: "Melbourne é famosa pelas 'quatro estações em um dia'. O frio e a chuva frequentes pedem comfort food, e a tendência é recorrer a opções pesadas. Eu ajudo você a montar refeições reconfortantes e nutritivas."
  },
  {
    icon: UtensilsCrossed,
    title: "Capital gastronômica e tentações",
    description: "Melbourne é conhecida como a capital gastronômica da Austrália. Da Lygon Street à Chapel Street, há muitas opções de restaurantes, cafés e brunches. Eu ajudo você a aproveitar essa cena sem perder de vista seus objetivos."
  },
  {
    icon: Leaf,
    title: "Cultura plant-based forte",
    description: "Em Melbourne, opções veganas e vegetarianas estão por toda parte. Se esse é o seu caminho, eu adapto o plano para que você tenha todos os nutrientes de que precisa."
  },
  {
    icon: Users,
    title: "Comunidade brasileira diversa",
    description: "A comunidade brasileira de Melbourne é grande e espalhada por regiões como South Yarra, St Kilda, Carlton, Footscray e Western Suburbs. O plano alimentar considera os mercados e as opções disponíveis na sua região."
  },
];

const passosMelbourne = [
  {
    icon: Calendar,
    title: "1. Você escolhe o horário",
    description: "A agenda online mostra os horários já no fuso de Melbourne. A consulta inicial é por videochamada e dura cerca de 30 minutos.",
  },
  {
    icon: Video,
    title: "2. Consulta por videochamada",
    description: "Avaliação completa, como numa consulta presencial. Você recebe o link do encontro por e-mail depois de agendar.",
  },
  {
    icon: MessageCircle,
    title: "3. Plano e acompanhamento",
    description: "O plano alimentar fica na plataforma e os ajustes acontecem pelo WhatsApp entre as consultas, com produtos do Coles, Woolworths, Aldi e dos mercados de bairro de Melbourne.",
  },
];

const beneficios = [
  "Plano alimentar com produtos de mercados de Melbourne (Coles, Woolworths, QV Market)",
  "Refeições adaptadas ao clima variável de Victoria",
  "Acesso à plataforma, com conteúdo prático",
  "Contato direto comigo por WhatsApp entre as consultas",
  "Opções plant-based e adaptações para restrições alimentares",
  "Consultas online no horário que funciona para você",
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button
      size="lg"
      variant="gradient"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      style={{ background: 'var(--gradient-green)' }}
      onClick={openDialog}
    >
      <Calendar className="mr-2 h-5 w-5" />
      Agendar consulta
    </Button>
    <Button
      size="lg"
      variant="gradient-outline"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
      onClick={() => window.open('https://wa.me/61423038977', '_blank')}
    >
      <MessageCircle className="mr-2 h-5 w-5" />
      Falar no WhatsApp
    </Button>
  </div>
);

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />

        <main>
          {/* HERO */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div
              className="absolute inset-0 opacity-15"
              style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                Clara Camille · Atendimento online
              </p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">
                Nutricionista brasileira em Melbourne
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">
                Acompanhamento nutricional online, em português, para brasileiros que moram em Melbourne. Plano alimentar pensado para o clima, os mercados e a rotina de Victoria.
              </p>
              <CTAButtons openDialog={openDialog} />
              <p className="text-sm text-muted-foreground font-sans">
                Consulta inicial de 30 minutos, sem custo.
              </p>
            </div>
          </section>

          {/* DESAFIOS DE MELBOURNE */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Vida em Melbourne
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Os desafios de comer bem morando em Melbourne
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
                {desafiosMelbourne.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-primary/5 hover:bg-primary/10 transition-colors">
                      <div className="flex-shrink-0">
                        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-primary/15 flex items-center justify-center">
                          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" strokeWidth={1.5} />
                        </div>
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* CONTEÚDO LOCAL */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-8 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Entendendo sua realidade
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground leading-tight">
                  Nutrição adaptada à vida em Melbourne
                </h2>
              </div>

              <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed max-w-3xl mx-auto text-center mb-8 sm:mb-12">
                Melbourne é multicultural, artística, cheia de cafés e tem um clima que muda a cada hora. Tudo isso influencia a alimentação de quem mora aí.
              </p>

              <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 max-w-3xl mx-auto">
                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <CloudRain className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Clima e comfort food</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Invernos frios pedem comida quente e pesada, e os dias de 40 °C no verão tiram o apetite. Eu ajusto o plano a cada estação.
                  </p>
                </div>

                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Mercados da cidade</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Queen Victoria Market, South Melbourne Market, os mercados asiáticos de Footscray e Springvale: o plano é montado com produtos acessíveis desses mercados.
                  </p>
                </div>

                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <UtensilsCrossed className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Plano para a sua rotina</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Mora em St Kilda, trabalha em hospitality no CBD, estuda na Monash ou na RMIT? Eu adapto o plano para caber no <span className="italic gradient-text font-medium">seu</span> dia a dia.
                  </p>
                </div>

                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <Leaf className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Café e vida social</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Dois ou três flat whites por dia, brunch no fim de semana? Não é preciso abrir mão disso: eu ajudo a encaixar esses hábitos nos seus objetivos.
                  </p>
                </div>
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* O QUE VOCÊ RECEBE */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  O que está incluído
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  O que você recebe no acompanhamento
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
                {beneficios.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-primary/5">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* CREDENCIAIS */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Quem te acompanha
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Conheça a Clara Camille
                </h2>
              </div>

              <div
                className="rounded-2xl sm:rounded-3xl overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(16px) saturate(1.3)',
                  WebkitBackdropFilter: 'blur(16px) saturate(1.3)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
                }}
              >
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-2/5 flex-shrink-0">
                    <div className="aspect-[3/4] sm:h-full w-full overflow-hidden">
                      <img
                        src={claraPortrait}
                        alt="Clara Camille, nutricionista brasileira para pacientes em Melbourne"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div className="sm:w-3/5 p-6 sm:p-10 flex flex-col justify-center space-y-6">
                    <div className="space-y-1">
                      <h3 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Clara Camille</h3>
                      <p className="text-sm sm:text-base text-primary font-typewriter font-semibold">Nutricionista · Austrália</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {[
                        { icon: GraduationCap, text: "Formação em Nutrição" },
                        { icon: Globe, text: "Atendimento em português" },
                        { icon: MapPin, text: "Online para todo o estado de VIC" },
                      ].map((cred, i) => (
                        <div key={i} className="flex items-start gap-2.5 p-3 rounded-xl bg-primary/5">
                          <cred.icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <span className="text-xs sm:text-sm text-foreground font-sans leading-snug">{cred.text}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                      Acompanho brasileiros em Melbourne que querem cuidar da alimentação com alguém que conhece a realidade de viver na Austrália. Tudo online, em português e sem julgamento.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* COMO FUNCIONA EM MELBOURNE */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Passo a passo
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Como funciona para quem mora em Melbourne
                </h2>
              </div>

              <div className="grid gap-4 sm:gap-6 sm:grid-cols-3">
                {passosMelbourne.map((item, index) => (
                  <div key={index} className="rounded-xl sm:rounded-2xl bg-background border border-border/50 p-5 sm:p-6 space-y-3">
                    <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-foreground font-sans">{item.title}</h3>
                    <p className="text-sm text-muted-foreground font-sans leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 sm:mt-10 rounded-xl sm:rounded-2xl bg-primary/5 p-5 sm:p-6">
                <div className="flex items-center gap-2.5 mb-2">
                  <Clock className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                  <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Horários no fuso de Melbourne</h3>
                </div>
                <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                  Atendo terça e sexta das 9h às 18h, quarta e quinta das 9h às 17h e sábado das 10h às 14h, no horário de Brisbane. De outubro a abril, com o horário de verão de Victoria, isso vira 10h às 19h, 10h às 18h e 11h às 15h em Melbourne. A agenda online já mostra os horários no seu fuso.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Tire suas dúvidas
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Dúvidas frequentes sobre nutrição em Melbourne
                </h2>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="rounded-xl border border-border/50 bg-card px-5 sm:px-6"
                  >
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-medium text-foreground hover:no-underline py-4 sm:py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* CTA FINAL */}
          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div
              className="absolute inset-0 blur-sm scale-105"
              style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
            <div className="absolute inset-0 opacity-80" style={{ background: 'var(--gradient-green)' }} />
            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div
                className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(24px) saturate(1.4)',
                  WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">
                  Mora em Melbourne? Vamos conversar
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">
                  Na consulta inicial, entendo sua rotina e seus objetivos e explico como o acompanhamento funciona online, no horário que for melhor para você.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto"
                    onClick={openDialog}
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Agendar consulta
                  </Button>
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto"
                    style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(12px)', color: 'white' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                    onClick={() => window.open('https://wa.me/61423038977', '_blank')}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Falar no WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* LINKS INTERNOS */}
          <section className="bg-card py-10 sm:py-14">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center space-y-4">
              <p className="text-sm text-muted-foreground font-sans">Navegue pelo site:</p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                <Link to="/" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Página inicial</Link>
                <span className="text-border">·</span>
                <Link to="/nutricionista-brasileira-na-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Nutricionista na Austrália</Link>
                <span className="text-border">·</span>
                <Link to="/nutricionista-brasileira-brisbane" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Nutricionista em Brisbane</Link>
                <span className="text-border">·</span>
                <Link to="/nutricionista-brasileira-sydney" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Nutricionista em Sydney</Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const NutricionistaBrasileiraMelbourne = () => (
  <ScheduleProvider>
    <PageContent />
  </ScheduleProvider>
);

export default NutricionistaBrasileiraMelbourne;
